import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Anchor, Button, Divider, Skeleton } from '@mantine/core';
import PropTypes from 'prop-types';
import { AlertCircle } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import AppCard from '../../common/AppCard';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import { useVendorBackPath } from '../../../helpers/vendorHelper';
import PageView from '../../common/PageView';
import BundleTypeSelect from '../bundles/BundleTypeSelect';
import {
  ECOM_PRODUCT_BUNDLE_TYPE_ENUM,
  ECOM_PRODUCT_PRICING_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import AppFlexbox from '../../common/AppFlexbox';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';
import StoreFixedBundleEdit from '../bundles/StoreFixedBundleEdit';
import AppImage from '../../common/AppImage';

const BUNDLE_EDIT_VIEWS = {
  [ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE]: StoreFixedBundleEdit
};

const initialFormState = {
  name: '',
  description: '',
  fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.DRAFT,
  fkEcomPricingType: ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT,
  pricingAmountOverride: null,
  roundingAdjustmentOverride: null,
  ecomVendorProductMedia: [],
  ecomVendorBundleProducts: []
};

const StoreBundleEditView = ({ duplicate }) => {
  const navigate = useNavigate();
  const { isTabletOrSmaller, isLaptopOrSmaller } = useMediaQueryIndex();
  const { pkEcomStoreProductPackage } = useParams();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchStoreProductPackage,
    createStoreProductPackages,
    updateStoreProductPackage,
    deleteStoreProductPackages,
    createStoreProducts
  } = useContext(StoreContext);
  const [bundleTypeState, setBundleTypeState] = useState({fkEcomBundleType: ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE});
  const ViewComponent = BUNDLE_EDIT_VIEWS[bundleTypeState.fkEcomBundleType];

  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(initialFormState, {
    confirmDiscard: true,
    containerWidth: 1100
  });
  const loading = !hasInitialized || state.ecomStoreProducts.loading;

  const { backPath } = useVendorBackPath(
    duplicate
      ? `/merchant/product-bundles/${pkEcomStoreProductPackage}`
      : `/merchant/product-bundles`
  );
  const ecomStoreProductPackage =
    pkEcomStoreProductPackage &&
    pkEcomStoreProductPackage ===
      state.ecomStoreProductPackage.value?.pkEcomStoreProductPackage.toString()
      ? state.ecomStoreProductPackage.value
      : null;

  const hasUnavailableProducts =
    ecomStoreProductPackage?.ecomVendorBundle.deleted ||
    ecomStoreProductPackage?.ecomVendorBundle.ecomVendorBundleProducts.some(
      (pb) =>
        pb.productDeleted ||
        pb.fkEcomProductStatus.toString() !== ECOM_PRODUCT_STATUS_ENUM.ACTIVE
    );

  const missingVendorProducts =
    ecomStoreProductPackage?.ecomVendorBundle.ecomVendorBundleProducts.filter(
      (pb) =>
        !ecomStoreProductPackage.ecomStoreProducts.some(
          (sp) =>
            sp.ecomVendorProduct.pkEcomVendorProduct === pb.fkEcomVendorProduct
        )
    ) || [];

  const isMissingStoreProducts = missingVendorProducts.length > 0;

  const isStoreBundle =
    ecomStoreProductPackage &&
    ecomStoreProductPackage.ecomVendorBundle.ecomStore?.pkEcomStore ===
      ecomStoreProductPackage.fkEcomStore;

  useEffect(() => {
    if (pkEcomStoreProductPackage && authState.pkEcomStore) {
      if (!ecomStoreProductPackage) {
        fetchStoreProductPackage(
          authState.pkEcomStore,
          pkEcomStoreProductPackage,
          (data) => {
            setBundleTypeState((c) => ({
              ...c,
              fkEcomBundleType: data.ecomVendorBundle.fkEcomBundleType.toString()
            }));
          }
        );
      }
      else {
        setBundleTypeState((c) => ({
          ...c,
          fkEcomBundleType: ecomStoreProductPackage.ecomVendorBundle.fkEcomBundleType.toString()
        }));
      }
    }
  }, [pkEcomStoreProductPackage, authState.pkEcomStore]);

  useEffect(() => {
    if (ecomStoreProductPackage) {
      resetFormState({
        name: duplicate
          ? `${ecomStoreProductPackage.ecomVendorBundle.name} (Copy)`
          : ecomStoreProductPackage.ecomVendorBundle.name,
        description: ecomStoreProductPackage.ecomVendorBundle.description,
        fkEcomProductStatus: ecomStoreProductPackage.fkEcomProductStatus.toString(),
        pricingAmountOverride: ecomStoreProductPackage.pricingAmountOverride
          ? ecomStoreProductPackage.pricingAmountOverride / 100
          : null,
        roundingAdjustmentOverride:
          ecomStoreProductPackage.roundingAdjustmentOverride,
        fkEcomPricingType:
          ecomStoreProductPackage.fkEcomPricingType?.toString() || null,
        ecomVendorProductMedia: ecomStoreProductPackage.ecomVendorBundle.ecomVendorProductMedia.map(
          (m) => ({
            ...m,
            key: m.pkEcomVendorProductMedia.toString(),
            src: m.src
          })
        ),
        ecomVendorBundleProducts: ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts.map(
          (p) => ({
            key: p.uuid,
            vendorProductUuid: p.uuid,
            count: p.count,
            sort: p.sort,
            ecomVendorProduct: {
              pkEcomVendorProduct: p.fkEcomVendorProduct,
              uuid: p.uuid,
              name: p.name,
              vendorName: p.vendorName,
              minPrice: p.minPrice,
              maxPrice: p.maxPrice,
              previewImage: p.previewImage
            }
          })
        )
      });
    }
    else {
      resetFormState({ ...initialFormState });
    }
  }, [ecomStoreProductPackage, duplicate]);

  const onSaveStoreProductPackage = () => {
    submitFormState((formData, onErrorCallback) => {
      const bundleData = {
        pricingAmountOverride: formData.pricingAmountOverride
          ? Math.round(formData.pricingAmountOverride * 100)
          : null,
        roundingAdjustmentOverride: formData.roundingAdjustmentOverride,
        fkEcomPricingType: formData.fkEcomPricingType,
        ecomVendorBundle: {
          name: formState.name,
          description: formState.description,
          fkEcomProductStatus: formState.fkEcomProductStatus,
          fkEcomBundleType: ECOM_PRODUCT_BUNDLE_TYPE_ENUM.FIXED_BUNDLE,
          ecomVendorProductMedia: [
            ...formState.ecomVendorProductMedia.map((m) => ({
              fkEcomVendorMedia: m.pkEcomVendorMedia,
              sort: m.sort,
              src: m.src
            })),
            ...(ecomStoreProductPackage && !duplicate
              ? ecomStoreProductPackage.ecomVendorBundle.ecomVendorProductMedia
                  .filter(
                    (m) =>
                      !formState.ecomVendorProductMedia.find(
                        (fm) => fm.pkEcomVendorMedia === m.fkEcomVendorMedia
                      )
                  )
                  .map((m) => ({
                    fkEcomVendorMedia: m.fkEcomVendorMedia,
                    deleted: true
                  }))
              : [])
          ],
          ecomVendorBundleProducts: formState.ecomVendorBundleProducts.map(
            (p) => ({
              count: p.count,
              sort: p.sort,
              uuid: p.vendorProductUuid
            })
          )
        }
      };

      if (ecomStoreProductPackage && !duplicate) {
        updateStoreProductPackage(
          ecomStoreProductPackage.pkEcomStoreProductPackage,
          bundleData,
          () => {
            triggerNotification('Bundle updated!', 'success');
          },
          onErrorCallback
        );
      }
      else {
        createStoreProductPackages(
          authState.pkEcomStore,
          { ecomStoreProductPackages: [bundleData] },
          (data) => {
            navigate(
              `/merchant/product-bundles/${data[0].pkEcomStoreProductPackage}`
            );
            triggerNotification('Bundle created!', 'success');
          },
          onErrorCallback
        );
      }
    });
  };

  return !pkEcomStoreProductPackage && !bundleTypeState.fkEcomBundleType ? (
    <PageView backPath={backPath} title="Select bundle type" width={1100}>
      <BundleTypeSelect
        onSelect={(value) =>
          setBundleTypeState({ ...bundleTypeState, fkEcomBundleType: value })
        }
        showUseCaseInfoCard
      />
    </PageView>
  ) : ViewComponent &&
    (ecomStoreProductPackage || !pkEcomStoreProductPackage) &&
    hasInitialized ? (
    <PageView
      backPath={backPath}
      formProps={{
        withFormControls: true,
        hasUnsavedChanges,
        isSubmitting,
        ...(!!ecomStoreProductPackage && !duplicate
          ? {
              deleteTitle: 'Delete bundle',
              onDelete: () => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomStoreProductPackage);
              }
            }
          : {}),
        onSubmit: (e) => {
          e.preventDefault();
          e.stopPropagation();
          onSaveStoreProductPackage();
        }
      }}
      onBackClick={(e) => {
        if (!pkEcomStoreProductPackage) {
          e.preventDefault();
          setBundleTypeState({ ...bundleTypeState, fkEcomBundleType: null });
        }
      }}
      rightSection={
        isStoreBundle &&
        !duplicate && (
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <Button
              color="dark"
              component={Link}
              disabled={isSubmitting}
              radius="md"
              size="xs"
              to={`/merchant/product-bundles/${ecomStoreProductPackage.pkEcomStoreProductPackage}/duplicate`}
              type="button"
              variant="light"
            >
              Duplicate
            </Button>
          </AppFlexbox>
        )
      }
      title={
        duplicate
          ? 'Duplicate bundle'
          : ecomStoreProductPackage?.ecomVendorBundle.name ??
            `Create fixed bundle`
      }
      width={1100}
    >
      {ecomStoreProductPackage && !isStoreBundle && (
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 0 }}
          withBorder
        >
          <AppFlexbox
            style={{
              padding: 8,
              gap: 8,
              backgroundColor: '#000',
              opacity: 0.8,
              color: '#fff',
              alignItems: 'center'
            }}
          >
            <AppStack>
              <AlertCircle size={18} />
            </AppStack>
            <AppText style={{ fontSize: 14 }}>Vendor bundle</AppText>
          </AppFlexbox>
          <AppStack style={{ padding: 8, gap: 5 }}>
            <AppText style={{ fontSize: 14 }}>
              This bundle is fully managed by the vendor.
            </AppText>
            <AppText
              style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
            >
              Bundle details and products included cannot be edited.
            </AppText>
          </AppStack>
        </AppCard>
      )}

      {duplicate && (
        <AppCard
          radius={isTabletOrSmaller ? 0 : 'md'}
          shadow="xs"
          style={{ padding: 8 }}
          withBorder
        >
          <AppFlexbox style={{ gap: 8 }}>
            <AppStack>
              <AlertCircle color="#eca70a" style={{ size: 18 }} />
            </AppStack>
            <AppText style={{ fontSize: 14 }}>
              Duplicate this bundle by making any change and clicking save.
            </AppText>
          </AppFlexbox>
        </AppCard>
      )}

      {!duplicate &&
        (hasUnavailableProducts ? (
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 0 }}
            withBorder
          >
            <AppFlexbox
              style={{
                padding: 8,
                gap: 8,
                backgroundColor: '#C40000',
                color: '#fff',
                alignItems: 'center'
              }}
            >
              <AppStack>
                <AlertCircle size={18} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>Bundle unavailable</AppText>
            </AppFlexbox>
            <AppStack style={{ padding: 8, gap: 5 }}>
              <AppText style={{ fontSize: 14 }}>
                This bundle contains products that are either deleted or
                inactive, and can’t be purchased until the unavailable products
                are removed from the bundle.
              </AppText>
            </AppStack>
          </AppCard>
        ) : (
          isMissingStoreProducts && (
            <AppCard
              radius={isTabletOrSmaller ? 0 : 'md'}
              shadow="xs"
              style={{ padding: 0 }}
              withBorder
            >
              <AppFlexbox
                style={{
                  padding: 8,
                  gap: 8,
                  backgroundColor: '#C40000',
                  color: '#fff',
                  alignItems: 'center'
                }}
              >
                <AppStack>
                  <AlertCircle size={18} />
                </AppStack>
                <AppText style={{ fontSize: 14 }}>Requires action</AppText>
              </AppFlexbox>
              <AppStack style={{ padding: 8, gap: 5 }}>
                <AppText style={{ fontSize: 14 }}>
                  One or more products in this bundle are not added to your
                  store. Please add the missing products to your store to make
                  this bundle available for purchase.
                </AppText>

                <AppFlexbox>
                  <Anchor
                    onClick={() => {
                      onOpenModal('add_missing_products');
                    }}
                    style={{ fontSize: 14, color: 'dodgerblue' }}
                  >
                    Add missing products
                  </Anchor>
                </AppFlexbox>
              </AppStack>
            </AppCard>
          )
        ))}

      <ViewComponent
        backPath={backPath}
        canEditProducts={isStoreBundle || !pkEcomStoreProductPackage}
        duplicate={duplicate}
        ecomStoreProductPackage={ecomStoreProductPackage}
        formState={formState}
        isSubmitting={isSubmitting}
        loading={loading}
        maxPrice={
          formState.ecomVendorBundleProducts.reduce(
            (r, c) => r + c.ecomVendorProduct.maxPrice * c.count,
            0
          ) || 0
        }
        minPrice={
          formState.ecomVendorBundleProducts.reduce(
            (r, c) => r + c.ecomVendorProduct.minPrice * c.count,
            0
          ) || 0
        }
        productUrl={
          ecomStoreProductPackage?.ecomStoreProduct ? (
            <Anchor
              component={Link}
              style={{ fontSize: 14, color: 'dodgerblue' }}
              to={`/merchant/products/${ecomStoreProductPackage.ecomStoreProduct.uuid}`}
            >
              View product
            </Anchor>
          ) : null
        }
        setFormState={setFormState}
      />

      {ConfirmDiscardModal}

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete bundle"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 12 }}>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to delete{' '}
              <span style={{ fontWeight: 500 }}>
                {ecomStoreProductPackage?.ecomVendorBundle.name}
              </span>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action can’t be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteStoreProductPackages(
            authState.pkEcomStore,
            [ecomStoreProductPackage?.pkEcomStoreProductPackage],
            () => {
              triggerNotification('Bundle deleted!', 'success');
              navigate('/merchant/product-bundles');
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${ecomStoreProductPackage?.ecomVendorBundle.name}?`}
      />

      <ConfirmModal
        confirmActionText="Yes, add products"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === 'add_missing_products'
        }
        message={
          <AppStack style={{ gap: 16 }}>
            <AppStack style={{ gap: 12 }}>
              <AppText style={{ fontSize: 14 }}>
                Are you sure you want to add the following missing products to
                your store?
              </AppText>

              <AppText
                style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
              >
                Products will be added with the same pricing as the bundle.
              </AppText>
            </AppStack>

            <Divider />

            {missingVendorProducts.map((pb) => (
              <React.Fragment key={pb.uuid}>
                <AppFlexbox style={{ gap: 8, alignItems: 'center' }}>
                  <AppCard
                    radius="md"
                    shadow="xs"
                    style={{ padding: 0, width: 60, height: 60 }}
                    withBorder
                  >
                    <AppStack style={{ width: '100%', height: '100%' }}>
                      <AppImage
                        src={pb.previewImage}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </AppStack>
                  </AppCard>
                  <AppStack style={{ flex: 1, gap: 0 }}>
                    <AppText style={{ fontSize: 14 }}>{pb.name}</AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      by: {pb.vendorName}
                    </AppText>
                  </AppStack>
                </AppFlexbox>
              </React.Fragment>
            ))}
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          createStoreProducts(
            authState.pkEcomStore,
            {
              storeProducts: missingVendorProducts.map((p) => ({
                fkEcomVendorProduct: p.fkEcomVendorProduct,
                createOrAssignCollections: true,
                pricingAmount: ecomStoreProductPackage.pricingAmountOverride,
                roundingAdjustment:
                  ecomStoreProductPackage.roundingAdjustmentOverride,
                fkEcomPricingType: ecomStoreProductPackage.fkEcomPricingType,
                fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.ACTIVE
              }))
            },
            () => {
              onChangeModalLoading(false);
              onCloseModal();
              triggerNotification('Products added!', 'success');
              fetchStoreProductPackage(
                authState.pkEcomStore,
                pkEcomStoreProductPackage
              );
            },
            (e) => {
              triggerNotification(e);
              onChangeModalLoading(false);
            }
          );
        }}
        title="Add missing bundle products?"
      />
    </PageView>
  ) : (
    <PageView backPath={backPath} loading width={1100}>
      <AppFlexbox
        style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
      >
        <AppStack style={{ flex: 2 }}>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>
        <AppStack style={{ flex: 1 }}>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
          <AppCard
            radius={isTabletOrSmaller ? 0 : 'md'}
            shadow="xs"
            style={{ padding: 16 }}
            withBorder
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="100%" />
              <Skeleton height={10} width="50%" />
            </AppStack>
          </AppCard>
        </AppStack>
      </AppFlexbox>
    </PageView>
  );
};

StoreBundleEditView.propTypes = { duplicate: PropTypes.bool };

export default StoreBundleEditView;
